<script setup lang="ts">
import TheLoader from '~/components/TheLoader.vue'
import BriefingCard from '~/components/market-insights/BriefingCard.vue'
import MarketInsightsPage from '~/components/market/MarketInsightsPage.vue'
import useAuthentication from '~/composables/useAuthentication'
import { useBriefings } from '~/composables/useBriefings'
import { ALL_MARKETS } from '~/constants/services/constants'
import type { TMarketSlug } from '~/src/types/markets'

definePageMeta({ layout: 'services' })

const route = useRoute()
const market = computed(() =>
  ALL_MARKETS.find(
    (m) =>
      m.slug === route.params.market ||
      m.alternativeSlug === route.params.market,
  ),
)
const title = computed(
  () => `${market.value.name} Market Analysis | Benchmark Mineral Intelligence`,
)

const { getBriefings } = useBriefings()
const { loggedIn } = useAuthentication()

const { data, status, refresh } = useAsyncData(async () => {
  const briefings = await getBriefings({
    market: route.params.market as TMarketSlug,
  })

  return { briefings }
})

const briefings = computed(() => data.value.briefings)
const isLoading = computed(() => status.value === 'pending')

watch(loggedIn, () => {
  refresh()
})

useSeoMeta({ title: () => title.value })
</script>

<template>
  <MarketInsightsPage :market="market">
    <TheLoader v-if="isLoading" />
    <div v-else>
      <div
        v-if="briefings.length"
        class="grid grid-cols-1 gap-6 md:grid-cols-2"
      >
        <BriefingCard
          v-for="briefing of briefings"
          :key="briefing.slug"
          :image="briefing.image"
          :title="briefing.archiveTitle"
          :publication-frequency="briefing.publicationFrequency"
          :is-new="briefing.isNew"
          :date="briefing.date"
          :link="briefing.link"
          :time-to-read="briefing.timeToRead"
          :can-read="briefing.viewerCanRead"
        />
      </div>
      <div
        v-else
        class="col-span-2 mt-10 w-full text-center text-2xl font-medium"
      >
        No briefings available at the moment.
      </div>
    </div>
  </MarketInsightsPage>
</template>
